import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { getStartOfWeek } from "src/Common/common";
import WOService from "src/Services/API/wo.service";
import TimeOffService from "src/Services/API/timeoff.service";
import UserService from "src/Services/API/user.service";
import { handleError } from "src/Common/function";
import { useNavigate } from "react-router-dom";
import WoChip from "./WoChip";
import WoMoreChip from "./WoMoreChip";
import { Avatar, fullName, typeUi, TYPE_UI } from "../timeoff/timeOffShared";

// Local midnight timestamp for a date, so day comparisons ignore time-of-day.
const dayStart = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

// "YYYY-MM-DD" in local time, for the time-off query range.
const toIsoDay = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const isSameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const WEEKDAYS = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];

// Content filter mirroring the mockup's segmented control.
const FILTERS = [
  { key: "all", label: "View All" },
  { key: "wo", label: "Work Orders" },
  { key: "off", label: "Time Off" },
];

// The Technicians calendar: one row per technician, seven day-columns for the
// selected week. Each cell shows the work orders assigned to that technician on
// that day plus any time-off (vacation/sick/etc.) covering it.
function CalendarTechnicians({ renderHeader, branch }) {
  const [weekStart, setWeekStart] = useState(() => getStartOfWeek(new Date()));
  const [filter, setFilter] = useState("all");

  const [technicians, setTechnicians] = useState([]);
  const [workOrders, setWorkOrders] = useState([]);
  const [requests, setRequests] = useState([]);

  // The seven dates of the visible week (Sun..Sat).
  const weekDays = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(weekStart);
      d.setDate(d.getDate() + i);
      return d;
    });
  }, [weekStart]);

  const monthForQuery = weekDays[3]; // mid-week day → majority month
  const year = monthForQuery.getFullYear();
  const month = monthForQuery.getMonth();

  const rangeLabel = `${weekDays[0].toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  })} – ${weekDays[6].toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`;

  // Load technicians, managers and project managers for the branch (enabled
  // only), merged and sorted by name. The role endpoint matches one role at a
  // time, so query each and dedupe by id.
  useEffect(() => {
    if (!branch) {
      setTechnicians([]);
      return;
    }
    const ROLES = ["Technician", "Manager", "Project Manager"];
    Promise.all(ROLES.map((role) => UserService.getUsersByRoleWithBranchId(role, branch)))
      .then((responses) => {
        const byId = new Map();
        for (const response of responses) {
          if (response.status !== 200) continue;
          for (const u of response.data || []) {
            if (u.isEnabled === true && !byId.has(u.id)) byId.set(u.id, u);
          }
        }
        const users = Array.from(byId.values()).sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        setTechnicians(users);
      })
      .catch((error) => handleError(error));
  }, [branch]);

  // Load all branch work orders overlapping the visible week. The calendar
  // endpoint already returns each WO with its assignedTechs[].user, so we group
  // by technician on the client.
  useEffect(() => {
    if (!branch) {
      setWorkOrders([]);
      return;
    }
    // Pull the whole month so adjacent weeks share the response; we filter to
    // the visible days client-side.
    WOService.getWOsForCalendar("Month", year, month, null, branch)
      .then((response) => {
        if (response.status === 200) setWorkOrders(response.data || []);
      })
      .catch((error) => handleError(error));
  }, [year, month, branch]);

  // Load time-off requests overlapping the visible month.
  useEffect(() => {
    if (!branch) {
      setRequests([]);
      return;
    }
    const start = new Date(year, month, 1);
    const end = new Date(year, month + 1, 0, 23, 59, 59, 999);
    const params = { startDate: toIsoDay(start), endDate: toIsoDay(end), branchId: branch };
    TimeOffService.getAll(params)
      .then((response) => {
        if (response.status === 200 || response.status === 201) {
          const data = response.data;
          const list = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
          // approved with PTO (1) / without PTO (2); hide pending and rejected.
          setRequests(list.filter((req) => req.status === 1 || req.status === 2));
        }
      })
      .catch((error) => handleError(error));
  }, [year, month, branch]);

  // Index WOs by technician id → date-string → [wo].
  const wosByTech = useMemo(() => {
    const map = new Map();
    for (const wo of workOrders) {
      if (!wo.startDate) continue;
      const techs = wo.assignedTechs || [];
      for (const t of techs) {
        // only count active assignments (status 1 = assigned)
        if (t.status !== 1) continue;
        const uid = t.user?.id;
        if (uid == null) continue;
        if (!map.has(uid)) map.set(uid, []);
        map.get(uid).push(wo);
      }
    }
    return map;
  }, [workOrders]);

  // Time-off requests for a technician, each clipped to the visible week and
  // expressed as a bar segment: { col, span, req, continuesLeft, continuesRight }.
  // `col` is the 0-based day index within the week; `span` is how many of the
  // seven day-columns it covers. continuesLeft/Right mark requests that extend
  // beyond the visible week (so the bar can render a flat, "cut off" edge).
  const offSegmentsForTech = useMemo(() => {
    const weekStartTs = dayStart(weekDays[0]);
    const weekEndTs = dayStart(weekDays[6]);
    return (techId) => {
      const segments = [];
      for (const req of requests) {
        if (req.user?.id !== techId || !req.startDate) continue;
        const s = new Date(req.startDate);
        const e = req.endDate ? new Date(req.endDate) : s;
        const startTs = dayStart(s);
        const endTs = dayStart(e);
        // skip requests that don't overlap the visible week at all
        if (endTs < weekStartTs || startTs > weekEndTs) continue;
        const clippedStart = Math.max(startTs, weekStartTs);
        const clippedEnd = Math.min(endTs, weekEndTs);
        const col = Math.round((clippedStart - weekStartTs) / 86400000);
        const span = Math.round((clippedEnd - clippedStart) / 86400000) + 1;
        segments.push({
          col,
          span,
          req,
          continuesLeft: startTs < weekStartTs,
          continuesRight: endTs > weekEndTs,
        });
      }
      // earlier-starting bars first so stacking order is stable
      segments.sort((a, b) => a.col - b.col || b.span - a.span);
      return segments;
    };
  }, [requests, weekDays]);

  const wosForTechDay = (techId, date) => {
    const list = wosByTech.get(techId) || [];
    return list.filter((wo) => isSameDay(new Date(wo.startDate), date));
  };

  const navigate = useNavigate();
  const openWo = (wo) => navigate(`/work/${wo.id}`);

  const handlePrev = () => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() - 7);
    setWeekStart(d);
  };
  const handleNext = () => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + 7);
    setWeekStart(d);
  };

  // Hoist a compact prev/next week header into the tabbar row.
  useEffect(() => {
    renderHeader?.(
      <div className="flex items-center gap-4 absolute right-0 bottom-2">
        <button onClick={handlePrev} className="text-blue-900 hover:opacity-80">
          <ChevronLeft size={16} />
        </button>
        <span className="font-OpenSansSemiBold text-[16px] leading-6 text-[#1A2332]">{rangeLabel}</span>
        <button onClick={handleNext} className="text-blue-900 hover:opacity-80">
          <ChevronRight size={16} />
        </button>
      </div>,
    );
    return () => renderHeader?.(null);
  }, [rangeLabel]);

  const showWO = filter === "all" || filter === "wo";
  const showOff = filter === "all" || filter === "off";

  // Renders a technician's work-order chips for one day: WO pills (up to 2) then
  // a "+N" overflow. Time-off is drawn separately as bars that span days.
  const renderCell = (tech, date) => {
    const wos = showWO ? wosForTechDay(tech.id, date) : [];

    const MAX_WO = 2;
    const namedWos = wos.slice(0, MAX_WO);
    const overflowWos = wos.slice(MAX_WO);
    const moreLabel = `Work Orders — ${date.toLocaleDateString("en-US", { month: "long", day: "numeric" })}`;

    return (
      <div className="flex flex-col gap-1">
        {namedWos.map((wo) => (
          <WoChip key={`wo-${wo.id}`} wo={wo} onOpen={openWo} />
        ))}
        {overflowWos.length > 0 && <WoMoreChip overflow={overflowWos} all={wos} label={moreLabel} onOpen={openWo} />}
      </div>
    );
  };

  // The time-off bars for a technician row, laid out over the seven day-columns
  // so a multi-day request spans its days as a single bar. Each segment occupies
  // its own grid row (via grid-auto-flow) so overlapping requests stack rather
  // than collide. Returns null when there's no time-off to show.
  const renderOffBars = (tech) => {
    if (!showOff) return null;
    const segments = offSegmentsForTech(tech.id);
    if (segments.length === 0) return null;
    return (
      <div className="grid grid-cols-7 gap-y-1 pt-2" style={{ gridAutoFlow: "row" }}>
        {segments.map((seg) => {
          const type = typeUi(seg.req.type);
          // square off the edge that runs past the visible week
          const rounding = `${seg.continuesLeft ? "rounded-l-none" : ""} ${seg.continuesRight ? "rounded-r-none" : ""}`;
          return (
            <div
              key={`off-${seg.req.id}`}
              className={`flex items-center gap-1 h-[24px] mx-2 rounded-full px-2 ${type.chip} ${rounding}`}
              style={{ gridColumn: `${seg.col + 1} / span ${seg.span}` }}
              title={`${type.label} — ${fullName(seg.req.user)}`}
            >
              <img src={type.icon} alt={type.label} className="w-3.5 h-3.5 shrink-0" />
              <span className="truncate font-OpenSansSemiBold text-[12px] leading-[18px]">{type.label}</span>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="py-2">
      {/* Legend + content filter */}
      <div className="flex items-center justify-between pb-4">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
          {Object.values(TYPE_UI).map((t) => (
            <div key={t.label} className="flex items-center gap-2">
              <span className={`inline-block w-3 h-3 rounded-[3px] ${t.dot}`} />
              <span className="font-OpenSansRegular text-[13px] leading-[18px] text-[#475569]">{t.label}</span>
            </div>
          ))}
          <div className="flex items-center gap-2">
            <span className="inline-block w-3 h-3 rounded-[3px] bg-[#BBF7D0]" />
            <span className="font-OpenSansRegular text-[13px] leading-[18px] text-[#475569]">Work Order</span>
          </div>
        </div>

        <div className="relative inline-flex items-center gap-1 rounded-lg border border-[#E5E8F3] bg-[#F8F9FE] p-1">
          {FILTERS.map((f) => {
            const isActive = filter === f.key;
            return (
              <button
                key={f.key}
                type="button"
                onClick={() => setFilter(f.key)}
                className={`relative z-10 rounded-md px-4 py-1.5 text-center font-OpenSansSemiBold text-[12px] leading-[18px] tracking-[0.12px] transition-colors ${
                  isActive
                    ? "bg-white text-[#1A2332] border border-[#EEF0F9] shadow-[0_1px_2px_0_rgba(0,0,0,0.05)]"
                    : "text-[#7C829A]"
                }`}
              >
                {f.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Grid header: technician column + 7 days */}
      <div className="grid grid-cols-[200px_repeat(7,minmax(0,1fr))] text-left border border-r-0 border-[#BFC2CE] bg-white">
        <div className="py-2 border-r border-[#BFC2CE] p-[11px]" />
        {weekDays.map((d) => {
          const today = isSameDay(d, new Date());
          return (
            <div
              key={d.toISOString()}
              className="py-2 border-r border-[#BFC2CE] p-[11px] text-center font-OpenSansRegular"
            >
              <div className="text-subTitleColor text-[13px] tracking-[-0.21px]">{WEEKDAYS[d.getDay()]}</div>
              <div className={`text-[16px] ${today ? "text-[#0065FD] font-extrabold" : "text-[#404A6E]"}`}>
                {d.getDate()}
              </div>
            </div>
          );
        })}
      </div>

      {/* Technician rows */}
      {technicians.length === 0 ? (
        <div className="border border-t-0 border-[#BFC2CE] bg-white py-10 text-center text-[14px] text-[#7C829A]">
          No technicians found for this branch.
        </div>
      ) : (
        technicians.map((tech) => (
          <div
            key={tech.id}
            className="grid grid-cols-[200px_minmax(0,1fr)] border border-t-0 border-r-0 border-[#BFC2CE] bg-white"
          >
            <div className="flex items-center gap-2 border-r border-[#BFC2CE] p-2 min-h-[56px]">
              <Avatar user={tech} className="w-7 h-7 shrink-0" />
              <span
                className="truncate font-OpenSansSemiBold text-[14px] leading-5 tracking-[0.14px] capitalize text-[#1A2332]"
                title={fullName(tech)}
              >
                {fullName(tech)}
              </span>
            </div>
            {/* Day area: a full-height background grid draws the column borders,
                with spanning time-off bars on top and per-day WO chips below. */}
            <div className="relative min-h-[56px]">
              {/* Column dividers, spanning the whole row height. */}
              <div className="absolute inset-0 grid grid-cols-7 pointer-events-none">
                {weekDays.map((d) => (
                  <div key={d.toISOString()} className="border-r border-[#BFC2CE]" />
                ))}
              </div>
              <div className="relative">
                {renderOffBars(tech)}
                <div className="grid grid-cols-7">
                  {weekDays.map((d) => (
                    <div key={d.toISOString()} className="p-2">
                      {renderCell(tech, d)}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  );
}

export default CalendarTechnicians;
